import React from 'react';
import { useTranslation } from 'react-i18next';
import './ClaimReceiptModal.css';

// Только 4 типа бустеров: VIP, Ранг, Лидер, Мини-игры (boost)
interface ClaimDetails {
  baseAmount: number;
  vipBonus?: {
    percentage: number;
    name: string;
    amount: number;
  } | null;
  rankBonus?: {
    percentage: number;
    name: string;
    amount: number;
  } | null;
  leaderBonus?: {
    percentage: number;
    name: string;
    amount: number;
  } | null;
  boostBonus?: {
    percentage: string | number;
    name?: string;
    amount: number;
  } | null;
  totalAmount: number;
}

interface ClaimReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimDetails: ClaimDetails | null;
}

const ClaimReceiptModal: React.FC<ClaimReceiptModalProps> = ({ isOpen, onClose, claimDetails }) => {
  const { t } = useTranslation();

  if (!isOpen || !claimDetails) return null;

  // Проверяем есть ли название статуса (для отображения) И бонус > 0 (для подсветки)
  // Check for percentage > 0 instead of comparing name strings (which may be in different languages)
  const hasVipName = claimDetails.vipBonus?.name && claimDetails.vipBonus.percentage > 0;
  const hasVipBonus = claimDetails.vipBonus && claimDetails.vipBonus.percentage > 0;

  const hasRankName = claimDetails.rankBonus?.name && claimDetails.rankBonus.percentage > 0;
  const hasRankBonus = claimDetails.rankBonus && claimDetails.rankBonus.percentage > 0;

  const hasLeaderName = claimDetails.leaderBonus?.name && claimDetails.leaderBonus.percentage > 0;
  const hasLeaderBonus = claimDetails.leaderBonus && claimDetails.leaderBonus.percentage > 0;

  const hasBoost = claimDetails.boostBonus && parseFloat(String(claimDetails.boostBonus.percentage)) > 0;

  return (
    <div className="claim-receipt-overlay" onClick={onClose}>
      <div className="claim-receipt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="claim-receipt-header">
          <span>{t('claimReceiptModal.title')}</span>
          <button className="claim-receipt-close" onClick={onClose}>×</button>
        </div>

        <div className="claim-receipt-body">
          <div className="claim-receipt-row">
            <span className="claim-receipt-label">{t('claimReceiptModal.baseAmount')}</span>
            <span className="claim-receipt-value">{claimDetails.baseAmount.toFixed(2)} SPERM</span>
          </div>

          {/* VIP статус - показываем название если есть */}
          <div className={`claim-receipt-row ${hasVipBonus ? 'highlight' : 'inactive'}`}>
            <span className="claim-receipt-label">
              {t('claimReceiptModal.vipLabel')} {hasVipName
                ? `${claimDetails.vipBonus!.name}${hasVipBonus ? ` (+${claimDetails.vipBonus!.percentage}%)` : ''}`
                : <span className="no-bonus">{t('claimReceiptModal.noStatus')}</span>}
            </span>
            <span className="claim-receipt-value">
              {hasVipBonus ? `+${claimDetails.vipBonus!.amount.toFixed(4)} SPERM` : '—'}
            </span>
          </div>

          {/* Ранг - показываем название если есть */}
          <div className={`claim-receipt-row ${hasRankBonus ? 'highlight' : 'inactive'}`}>
            <span className="claim-receipt-label">
              {t('claimReceiptModal.rankLabel')} {hasRankName
                ? `${claimDetails.rankBonus!.name}${hasRankBonus ? ` (+${claimDetails.rankBonus!.percentage}%)` : ''}`
                : <span className="no-bonus">{t('claimReceiptModal.noRank')}</span>}
            </span>
            <span className="claim-receipt-value">
              {hasRankBonus ? `+${claimDetails.rankBonus!.amount.toFixed(4)} SPERM` : '—'}
            </span>
          </div>

          {/* Лидер статус - показываем название если есть */}
          <div className={`claim-receipt-row ${hasLeaderBonus ? 'highlight' : 'inactive'}`}>
            <span className="claim-receipt-label">
              {t('claimReceiptModal.leaderLabel')} {hasLeaderName
                ? `${claimDetails.leaderBonus!.name}${hasLeaderBonus ? ` (+${claimDetails.leaderBonus!.percentage}%)` : ''}`
                : <span className="no-bonus">{t('claimReceiptModal.noStatus')}</span>}
            </span>
            <span className="claim-receipt-value">
              {hasLeaderBonus ? `+${claimDetails.leaderBonus!.amount.toFixed(4)} SPERM` : '—'}
            </span>
          </div>

          {/* Буст мини-игры - всегда показываем */}
          <div className={`claim-receipt-row ${hasBoost ? 'highlight' : 'inactive'}`}>
            <span className="claim-receipt-label">
              {t('claimReceiptModal.gamesLabel')} {hasBoost
                ? `(+${claimDetails.boostBonus!.percentage}%)`
                : <span className="no-bonus">{t('claimReceiptModal.noBoost')}</span>}
            </span>
            <span className="claim-receipt-value">
              {hasBoost ? `+${claimDetails.boostBonus!.amount.toFixed(4)} SPERM` : '—'}
            </span>
          </div>

          <div className="claim-receipt-divider"></div>

          <div className="claim-receipt-row total">
            <span className="claim-receipt-label">{t('claimReceiptModal.totalReceived')}</span>
            <span className="claim-receipt-value">{claimDetails.totalAmount.toFixed(2)} SPERM</span>
          </div>
        </div>

        <button className="claim-receipt-ok-btn" onClick={onClose}>
          {t('claimReceiptModal.close')}
        </button>
      </div>
    </div>
  );
};

export default ClaimReceiptModal;
